import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { FinalCta } from "@/components/FinalCta";
import { api } from "../lib/api";
import { useLanguage, dfLocale } from "../i18n";

export default function Blog() {
  const { t, lang } = useLanguage();
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api.get(`/posts?lang=${lang}`).then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, [lang]);

  return (
    <>
      <Seo
        title={`Blog — Alsera Studio`}
        description={t("Consigli digitali pratici per piccole imprese: siti web, presenza online e strumenti utili spiegati in modo semplice.")}
      />
      <section className="pt-32 pb-14 sm:pt-40" data-testid="blog-hero">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">Blog</p>
            <h1 className="mt-4 max-w-3xl text-4xl font-extrabold leading-[1.1] tracking-tight text-antracite sm:text-5xl" data-testid="blog-title">
              {t("Consigli digitali per la tua attività.")}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-antracite/70 sm:text-lg">
              {t("Guide pratiche e consigli utili per migliorare la presenza online della tua piccola impresa.")}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 sm:pb-28" data-testid="blog-list">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          {posts === null ? null : posts.length === 0 ? (
            <p className="card-soft p-8 text-sm text-antracite/60" data-testid="blog-empty">{t("Nessun articolo disponibile al momento.")}</p>
          ) : (
            <div className="grid gap-6 md:grid-cols-3">
              {posts.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.08}>
                  <article className="group card-soft flex h-full flex-col justify-between p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-petrolio/10" data-testid={`blog-card-${i}`}>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-antracite/50">
                        {format(parseISO(p.created_at), "d MMMM yyyy", { locale: dfLocale[lang] })} · {p.minutes} {t("min di lettura")}
                      </p>
                      <h2 className="mt-3 text-lg font-bold leading-snug tracking-tight text-antracite sm:text-xl">{p.title}</h2>
                      <p className="mt-3 text-sm leading-relaxed text-antracite/65">{p.excerpt}</p>
                    </div>
                    <Link
                      to={`/blog/${p.slug}`}
                      data-testid={`blog-read-${i}`}
                      className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-elettrico transition-all duration-300 hover:gap-3.5"
                    >
                      {t("Leggi l'articolo")}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <FinalCta />
    </>
  );
}
